import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import { PERMISSION_MODES, PermissionMode } from "@cohesive/api-client";
import {
  Check,
  ChevronDown,
  Hand,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { PermissionModeControl } from "../hooks/usePermissionModeControl";
import {
  FOOTER_TRIGGER_CLASS,
  footerMenuPlacement,
  MENU_CLASS,
  MenuSide,
  ROW_CLASS,
} from "./menuStyles";

interface PermissionModeOption {
  value: PermissionMode;
  label: string;
  description: string;
  icon: typeof Hand;
}

const OPTIONS: PermissionModeOption[] = [
  {
    value: PERMISSION_MODES.CONFIRM_MUTATIONS,
    label: "Manual",
    description: "Every mutating command asks for confirmation",
    icon: Hand,
  },
  {
    value: PERMISSION_MODES.AUTO,
    label: "Auto",
    description: "The agent decides; risky commands still ask",
    icon: ShieldCheck,
  },
  {
    value: PERMISSION_MODES.ALLOW_ALL,
    label: "Allow all",
    description: "Auto-accept all commands this session",
    icon: TriangleAlert,
  },
];

interface ConfirmationModeButtonProps {
  control: PermissionModeControl;
  /** Whether Allow all spells out what it means beside its label. */
  explain?: boolean;
  menuSide?: MenuSide;
}

/**
 * Permission mode ("permissions") for the session this composer is attached to, on the line under
 * the box. The mode is server state on the flow session — `permission_mode` — but the switch rides
 * the next message rather than a write of its own, so the control drives a
 * {@link PermissionModeControl} and the composer stamps the pick onto the send.
 *
 * Click-only: there is no keyboard shortcut. Allow all turns the trigger amber (`lc-warning`, the
 * design system's caution token) and says what it means, which is the only allow-all notice the
 * composer shows. With `control.mode` undefined — no session object in hand, and no draft to drive —
 * it renders disabled and dimmed, so nothing shifts when the session arrives.
 */
export function ConfirmationModeButton({
  control,
  explain = true,
  menuSide = "top",
}: ConfirmationModeButtonProps) {
  const [open, setOpen] = useState(false);
  const placement = footerMenuPlacement(menuSide);

  const active = OPTIONS.find((option) => option.value === control.mode);
  const isUnknown = !active;
  const allowAll = active?.value === PERMISSION_MODES.ALLOW_ALL;
  const TriggerIcon = active?.icon ?? ShieldCheck;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isUnknown}
          aria-label={
            isUnknown ? "Permission mode" : `Permission mode: ${active.label}`
          }
          className={cn(
            FOOTER_TRIGGER_CLASS,
            allowAll
              ? "bg-lc-warning/10 text-lc-warning hover:bg-lc-warning/15"
              : "text-n-60 hover:bg-accent hover:text-n-90",
            isUnknown && "opacity-40",
          )}
        >
          <TriggerIcon className="size-3.5 shrink-0" />
          <span className="font-medium whitespace-nowrap">
            {active?.label ?? "Auto"}
          </span>
          {allowAll && explain && (
            <span className="truncate max-[419px]:hidden">
              · commands run without asking
            </span>
          )}
          <ChevronDown className="size-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        side={placement.side}
        avoidCollisions={placement.avoidCollisions}
        className={cn(MENU_CLASS, placement.className)}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => {
              setOpen(false);
              if (option.value !== control.mode) {
                trackComposerControlChanged({
                  control: "confirmation_mode",
                  value: option.value,
                });
              }
              control.setMode(option.value);
            }}
            className={cn(
              ROW_CLASS,
              control.mode === option.value && "bg-accent/60",
            )}
          >
            <option.icon
              className={cn(
                "size-4 shrink-0",
                option.value === PERMISSION_MODES.ALLOW_ALL &&
                  "text-lc-warning",
              )}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm">{option.label}</div>
              <div className="text-xs text-muted-foreground leading-snug">
                {option.description}
              </div>
            </div>
            {control.mode === option.value && (
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
